"use client";
import { useEffect } from "react";
import { Control, Controller, UseFormClearErrors } from "react-hook-form";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import {
  IconUpload,
  IconCar,
  IconFileText,
  IconPhoto,
} from "@tabler/icons-react";
import YearPicker from "../../../components/YearPicker";
import MultiFileUploader from "../../../components/MultipleFilesUploader";
import { getFileExtension } from "../../../utilities/helper";
import { T, useLanguage } from "../../../context/LanguageContext";

const BASE_URL = process.env.REACT_APP_BASE_URL || "";

export const SystemTypes = [
  { value: "phone", text: "Phone" },
  { value: "email", text: "Email" },
  { value: "whatsapp", text: "WhatsApp" },
];

const vehicleTypes = [
  { id: 0, value: "bike", text: "Bike/Motorcycle" },
  { id: 1, value: "car", text: "Car/Sedan" },
  { id: 2, value: "van", text: "Van/Cargo Van" },
  { id: 3, value: "pickup", text: "Pickup Truck" },
  { id: 4, value: "truck", text: "Truck/Lorry" },
  { id: 5, value: "heavy_truck", text: "Heavy Truck" },
  { id: 6, value: "trailer", text: "Trailer" },
  { id: 7, value: "three_wheeler", text: "Three Wheeler" },
];

const fuelTypes = [
  { id: 0, value: "petrol", text: "Petrol" },
  { id: 1, value: "diesel", text: "Diesel" },
  { id: 2, value: "electric", text: "Electric" },
  { id: 3, value: "hybrid", text: "Hybrid" },
  { id: 4, value: "cng", text: "CNG" },
  { id: 5, value: "lpg", text: "LPG" },
  { id: 6, value: "hydrogen", text: "Hydrogen" },
  { id: 7, value: "biofuel", text: "Biofuel" },
];
const regType = [
  { id: 0, value: "private", text: "Private" },
  { id: 1, value: "commercial", text: "Commercial" },
];

const insuranceCompany = [
  { id: 0, value: "private", text: "Tawuniya" },
  { id: 1, value: "commercial", text: "Bupa Arabia" },
  { id: 2, value: "commercial", text: "Al Rajhi Takaful" },
  { id: 3, value: "commercial", text: "Allianz Saudi Fransi" },
  { id: 4, value: "commercial", text: "Liva Insurance" },
  { id: 5, value: "commercial", text: "Malath Cooperative Insurance" },
  { id: 6, value: "commercial", text: "MedGulf" },
  { id: 7, value: "commercial", text: "Walaa Cooperative Insurance" },
  { id: 8, value: "commercial", text: "Gulf Union Al Ahlia" },
  { id: 9, value: "commercial", text: "Arabian Shield Cooperative Insurance" },
  { id: 10, value: "commercial", text: "Aljazira Takaful" },
  { id: 11, value: "commercial", text: "Chubb Arabia" },
  { id: 12, value: "commercial", text: "Amana Cooperative Insurance" },
  { id: 13, value: "commercial", text: "Salama Cooperative Insurance" },
];

type FilesType = {
  images: boolean;
  imagesOdoMeter: boolean;
  rcDocs: boolean;
  [key: string]: boolean | undefined; // Allow additional keys if needed
};

type Props = {
  register: any;
  setValue: any;
  watch: any;
  errors: any;
  clearErrors: UseFormClearErrors<any>;
  control: Control<any>;
  defaultValues?: any;
  setSelectedFiles?: (val: any) => void;
  selectedFiles?: FilesType;
};

const AddFleetModal = ({
  register,
  errors,
  setValue,
  watch,
  control,
  clearErrors,
  setSelectedFiles,
}: Props) => {
  const { t } = useLanguage();
  // Sync manual Company (makeName) & Model (modelName) input values to name and model fields
  const watchMakeName = watch("makeName");
  const watchModelName = watch("modelName");

  useEffect(() => {
    setValue(
      "model",
      watchMakeName && watchModelName
        ? `${watchMakeName} ${watchModelName}`
        : watchMakeName || watchModelName || "",
    );
    setValue("name", watchModelName || "");
  }, [watchMakeName, watchModelName, setValue]);

  // Section header component
  const SectionHeader = ({
    icon: Icon,
    title,
    className = "border-b border-[#FBD037]/30 pb-2 mb-3",
  }: {
    icon: any;
    title: string;
    className?: string;
  }) => (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        <T>{title}</T>
      </p>
    </div>
  );

  return (
    <div className="space-y-2 p-4">
      {/* Vehicle Details Section */}
      <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl   p-4 transition-all duration-300 space-y-3 relative">
        <SectionHeader icon={IconCar} title="Vehicle Details" />
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

        <div className="grid grid-cols-6 gap-4">
          <input
            type="hidden"
            {...register("name", { required: "Vehicle Name is required" })}
            value={watch("name") ?? ""}
          />

          <NewinputField
            className="space-y-1 col-span-2"
            name="makeName"
            type="text"
            title="Company"
            placeholder="Enter Company"
            register={register}
            error={errors.makeName}
            validation={{ required: "Company is required" }}
          />

          <NewSelectInput
            className="col-span-2"
            name="vehicleType"
            label="Type"
            placeholder="Select Vehicle Type"
            selectOptions={vehicleTypes}
            clearErrors={clearErrors}
            register={register}
            error={errors.vehicleType}
            validation={{ required: "Vehicle type is required" }}
            value={watch("vehicleType")}
            setValue={(val: string) => {
              setValue("vehicleType", val);
            }}
            fullWidthDropdown={true}
          />

          <NewinputField
            className="space-y-1 col-span-2"
            name="modelName"
            type="text"
            title="Model"
            placeholder="Enter Model"
            register={register}
            error={errors.modelName}
            validation={{ required: "Model is required" }}
          />

          <Controller
            control={control}
            name="year"
            rules={{ required: "Year is required" }}
            render={({ field }) => (
              <YearPicker
                className="space-y-1 col-span-2"
                value={field.value}
                onChange={(val) => {
                  field.onChange(val);
                }}
                error={errors.year}
                required={true}
              />
            )}
          />

          {/* Hidden fields for RHF & backend mapping */}
          <input
            type="hidden"
            {...register("makeId")}
            value={watch("makeId") ?? ""}
          />
          <input
            type="hidden"
            {...register("modelId")}
            value={watch("modelId") ?? ""}
          />
          <input
            type="hidden"
            {...register("model")}
            value={watch("model") ?? ""}
          />

          <NewSelectInput
            className="col-span-2"
            name="fuelType"
            label="Fuel"
            placeholder="Select Fuel Type"
            selectOptions={fuelTypes}
            clearErrors={clearErrors}
            register={register}
            error={errors.fuelType}
            value={watch("fuelType")?.toLowerCase()}
            setValue={(val: string) => {
              setValue("fuelType", val);
            }}
            fullWidthDropdown={true}
          />

          <NewinputField
            className="space-y-1 col-span-2"
            name="vehicleNumber"
            type="text"
            title="Plate No"
            placeholder="ABC 1234 or 1234 ABC"
            register={register}
            error={errors.vehicleNumber}
            validation={{
              required: "Vehicle No is required",
              pattern: {
                value:
                  /^(?:[a-zA-Z]{3}\s?[0-9]{1,4}|[0-9]{1,4}\s?[a-zA-Z]{3})$/,
                message:
                  "Vehicle number must be 3 letters and 1 to 4 digits (e.g. ABC 1234 or 1234 ABC)",
              },
            }}
          />
          <NewSelectInput
            className="col-span-2"
            name="registrationType"
            label="Reg type"
            placeholder="Private"
            selectOptions={regType}
            clearErrors={clearErrors}
            register={register}
            error={errors.registrationType}
            value={watch("registrationType")}
            setValue={(val: string) => {
              setValue("registrationType", val);
            }}
            fullWidthDropdown={true}
          />
          <NewinputField
            className="space-y-1 col-span-2"
            name="color"
            type="text"
            title="Color"
            placeholder="black"
            register={register}
            error={errors.color}
          />
          <NewinputField
            className="space-y-1 col-span-2"
            name="sequenceNumber"
            type="text"
            title="Seq No"
            placeholder="1234567890"
            register={register}
            error={errors.sequenceNumber}
            validation={{
              required: "Seq No is required",
            }}
          />
        </div>
      </div>

      {/* Uploads Details Section */}
      <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl  p-4 transition-all duration-300 space-y-3 relative">
        <SectionHeader icon={IconFileText} title="Uploads Details" />
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

        <div className="grid grid-cols-2 gap-4">
          <div className="grid grid-cols-[1fr_auto] gap-2 items-start">
            <NewinputField
              name="odometer"
              type="number"
              title="Odometer (KM)"
              placeholder="e.g. 85000"
              register={register}
              // error={errors.odometer}
              // validation={{
              //   required: "Odometer is required",
              //   min: {
              //     value: 0,
              //     message: "Odometer cannot be negative",
              //   },
              //   max: {
              //     value: 2000000,
              //     message: "Odometer value looks unrealistic",
              //   },
              // }}
            />
            <Controller
              name="imagesOdoMeter"
              control={control}
              rules={{
                required: "Odometer",
                validate: (file: File) => {
                  if (!file) return "Please select a file";

                  const MAX_SIZE = 1 * 1024 * 1024;
                  if (file.size > MAX_SIZE) {
                    return "File size must be 1 MB or less";
                  }

                  const allowedTypes = [
                    "image/jpeg",
                    "image/png",
                    "image/jpg",
                    "application/pdf",
                  ];

                  if (
                    file.type
                      ? !allowedTypes.includes(file.type)
                      : !["jpeg", "png", "jpg", "pdf"].includes(
                          getFileExtension(file as any),
                        )
                  ) {
                    return "Only JPG, JPEG, PNG, or PDF files are allowed";
                  }

                  return true;
                },
              }}
              render={({ field, fieldState }) => {
                const fileName =
                  field.value instanceof File
                    ? field.value.name
                    : typeof field.value === "string" && field.value
                      ? decodeURIComponent(field.value.split("/").pop() || "")
                      : "";
                return (
                  <div className="flex flex-col gap-1 w-[46px]">
                    <label htmlFor="" className="invisible">
                      u
                    </label>
                    <label className="flex items-center justify-center py-[5px] mt-2 px-2 border border-[#D2D2D2] rounded-xl cursor-pointer bg-[#FBD037]/20 hover:bg-[#FBD037]/30 transition-colors">
                      <IconUpload stroke={1} size={16} />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            field.onChange(file);
                            if (setSelectedFiles) {
                              setSelectedFiles((prev: any) => ({
                                ...prev,
                                imagesOdoMeter: true,
                              }));
                            }
                          }
                          clearErrors("imagesOdoMeter");
                        }}
                      />
                    </label>
                    {fieldState.error ? (
                      <p className="text-red-500 text-xs leading-tight whitespace-nowrap absolute mt-14">
                        {fieldState.error.message
                          ? t(fieldState.error.message)
                          : ""}
                      </p>
                    ) : fileName ? (
                      <></>
                    ) : // <p
                    //   className="text-green-600 text-xs leading-tight truncate max-w-[100px] absolute mt-14"
                    //   title={fileName}
                    // >
                    //   {t("✓ Uploaded")}
                    // </p>
                    null}
                  </div>
                );
              }}
            />
          </div>

          <div className="grid grid-cols-[1fr_auto] gap-2 items-start">
            <NewinputField
              name="rcNumber"
              type="text"
              title="Vechile Id"
              placeholder="1234567890"
              register={register}
              error={errors.rcNumber}
              validation={{
                required: "Vehicle ID is required",
                minLength: {
                  value: 3,
                  message: "Vehicle ID must be at least 3 characters",
                },
              }}
            />
            <Controller
              name="rcDocs"
              control={control}
              rules={{
                required: "RC Doc",
                validate: (file: File) => {
                  if (!file) return "Please select a file";

                  const MAX_SIZE = 1 * 1024 * 1024;
                  if (file.size > MAX_SIZE) {
                    return "File size must be 1 MB or less";
                  }

                  const allowedTypes = [
                    "image/jpeg",
                    "image/png",
                    "image/jpg",
                    "application/pdf",
                  ];

                  if (
                    file.type
                      ? !allowedTypes.includes(file.type)
                      : !["jpeg", "png", "jpg", "pdf"].includes(
                          getFileExtension(file as any),
                        )
                  ) {
                    return "Only JPG, JPEG, PNG, or PDF files are allowed";
                  }

                  return true;
                },
              }}
              render={({ field, fieldState }) => {
                const fileName =
                  field.value instanceof File
                    ? field.value.name
                    : typeof field.value === "string" && field.value
                      ? decodeURIComponent(field.value.split("/").pop() || "")
                      : "";
                return (
                  <div className="flex flex-col gap-1 w-[46px]">
                    <label htmlFor="" className="invisible">
                      u
                    </label>
                    <label className="flex items-center justify-center py-[5px] mt-2 px-2 border border-[#D2D2D2] rounded-xl cursor-pointer bg-[#FBD037]/20 hover:bg-[#FBD037]/30 transition-colors">
                      <IconUpload stroke={1} size={16} />
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            field.onChange(file);
                            if (setSelectedFiles) {
                              setSelectedFiles((prev: any) => ({
                                ...prev,
                                rcDocs: true,
                              }));
                            }
                            clearErrors("rcDocs");
                          }
                        }}
                      />
                    </label>
                    {fieldState.error ? (
                      <p className="text-red-500 text-xs leading-tight whitespace-nowrap absolute mt-14">
                        {fieldState.error.message
                          ? t(fieldState.error.message)
                          : ""}
                      </p>
                    ) : fileName ? (
                      <></>
                    ) : null}
                  </div>
                );
              }}
            />
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-2 items-start">
            <NewinputField
              name="inspectionExpiryDate"
              type="date"
              title="Inspection Exp date"
              register={register}
              error={errors.inspectionExpiryDate}
            />
            <Controller
              name="inspectionPaper"
              control={control}
              render={({ field, fieldState }) => {
                const fileName =
                  field.value instanceof File
                    ? field.value.name
                    : typeof field.value === "string" && field.value
                      ? decodeURIComponent(field.value.split("/").pop() || "")
                      : "";
                return (
                  <div className="flex flex-col gap-1 w-[46px]">
                    <label htmlFor="" className="invisible">
                      u
                    </label>
                    <label className="flex items-center justify-center py-[5px] mt-2 px-2 border border-[#D2D2D2] rounded-xl cursor-pointer bg-[#FBD037]/20 hover:bg-[#FBD037]/30 transition-colors">
                      <IconUpload stroke={1} size={16} />
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            field.onChange(file);
                            if (setSelectedFiles) {
                              setSelectedFiles((prev: any) => ({
                                ...prev,
                                inspectionPaper: true,
                              }));
                            }
                            clearErrors("inspectionPaper");
                          }
                        }}
                      />
                    </label>
                    {fieldState.error ? (
                      <p className="text-red-500 text-xs leading-tight whitespace-nowrap absolute mt-14">
                        {fieldState.error.message
                          ? t(fieldState.error.message)
                          : ""}
                      </p>
                    ) : fileName ? (
                      <></>
                    ) : null}
                  </div>
                );
              }}
            />
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-2 items-start">
            <NewinputField
              name="registrationExpiryDate"
              type="date"
              title="Vechile Reg Exp date"
              register={register}
              error={errors.registrationExpiryDate}
            />
            <Controller
              name="registrationDocumentUrl"
              control={control}
              render={({ field, fieldState }) => {
                const fileName =
                  field.value instanceof File
                    ? field.value.name
                    : typeof field.value === "string" && field.value
                      ? decodeURIComponent(field.value.split("/").pop() || "")
                      : "";
                return (
                  <div className="flex flex-col gap-1 w-[46px]">
                    <label htmlFor="" className="invisible">
                      u
                    </label>
                    <label className="flex items-center justify-center py-[5px] mt-2 px-2 border border-[#D2D2D2] rounded-xl cursor-pointer bg-[#FBD037]/20 hover:bg-[#FBD037]/30 transition-colors">
                      <IconUpload stroke={1} size={16} />
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            field.onChange(file);
                            if (setSelectedFiles) {
                              setSelectedFiles((prev: any) => ({
                                ...prev,
                                registrationDocumentUrl: true,
                              }));
                            }
                            clearErrors("registrationDocumentUrl");
                          }
                        }}
                      />
                    </label>
                    {fieldState.error ? (
                      <p className="text-red-500 text-xs leading-tight whitespace-nowrap absolute mt-14">
                        {fieldState.error.message
                          ? t(fieldState.error.message)
                          : ""}
                      </p>
                    ) : fileName ? (
                      <></>
                    ) : null}
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>
      <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl p-4 transition-all duration-300 space-y-3 relative">
        <SectionHeader icon={IconPhoto} title="Insurance Details" />
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div className="grid grid-cols-3 gap-2">
          <NewSelectInput
            className="w-full"
            name="insuranceCompany"
            label="Company"
            placeholder="Tawuniya"
            selectOptions={insuranceCompany}
            clearErrors={clearErrors}
            register={register}
            error={errors.insuranceCompany}
            value={watch("insuranceCompany")}
            setValue={(val: string) => {
              setValue("insuranceCompany", val);
            }}
            fullWidthDropdown={true}
          />

          <NewinputField
            name="insuranceStartDate"
            type="date"
            title="Start Date"
            placeholder="black"
            register={register}
            error={errors.insuranceStartDate}
          />
          
          <div className="grid grid-cols-[1fr_auto] gap-2 items-start col-span-1">
            <NewinputField
              name="insuranceEndDate"
              type="date"
              title="Exp Date"
              register={register}
              error={errors.insuranceEndDate}
            />
            <Controller
              name="insuranceDocumentUrl"
              control={control}
              render={({ field, fieldState }) => {
                const fileName =
                  field.value instanceof File
                    ? field.value.name
                    : typeof field.value === "string" && field.value
                      ? decodeURIComponent(field.value.split("/").pop() || "")
                      : "";
                return (
                  <div className="flex flex-col gap-1 w-[46px]">
                    <label htmlFor="" className="invisible">
                      u
                    </label>
                    <label className="flex items-center justify-center py-[5px] mt-2 px-2 border border-[#D2D2D2] rounded-xl cursor-pointer bg-[#FBD037]/20 hover:bg-[#FBD037]/30 transition-colors">
                      <IconUpload stroke={1} size={16} />
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            field.onChange(file);
                            if (setSelectedFiles) {
                              setSelectedFiles((prev: any) => ({
                                ...prev,
                                insuranceDocumentUrl: true,
                              }));
                            }
                            clearErrors("insuranceDocumentUrl");
                          }
                        }}
                      />
                    </label>
                    {fieldState.error ? (
                      <p className="text-red-500 text-xs leading-tight whitespace-nowrap absolute mt-14">
                        {fieldState.error.message
                          ? t(fieldState.error.message)
                          : ""}
                      </p>
                    ) : fileName ? (
                      <></>
                    ) : null}
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>
      {/* Vehicle Pictures Section */}
      <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl p-4 transition-all duration-300 space-y-3 relative">
        <div className="flex items-center gap-4 justify-between">
          <SectionHeader icon={IconPhoto} title="Pictures" className="" />
          <MultiFileUploader
            control={control}
            name="images"
            maxFiles={4}
            allowedTypes={["image/jpeg", "image/png", "image/jpg"]}
            setSelectedFiles={setSelectedFiles}
            existingImages={watch("images")}
          />
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        </div>
      </div>
    </div>
  );
};

export default AddFleetModal;
